// Per-user SQL-function pick frequency → autocomplete ranking. Every time the
// user accepts a built-in function from the editor popup, its count is bumped;
// the ranking blends a base popularity with the personal count so the functions
// you pick most rise to the top of a matching search (e.g. typing `DATE` shows
// DATE_SUB above DATE_FORMAT once you've used DATE_SUB more).
//
// Persisted as a single per-user pref (`editor.funcUsage`, a small JSON object
// of the top-N counts) — debounced so accepting a completion doesn't spam the
// server. Loaded at login (auth-store) like the column-rename dictionary.
import { api } from './api.js';
import { FUNCTIONS } from './sqlfunctions.js';

const KEY = 'editor.funcUsage';
const SAVE_TOP_N = 80;   // cap the persisted map so it stays under the pref size limit
const SAVE_DEBOUNCE = 4000;

const basePop = new Map(FUNCTIONS.map((f) => [f.name, f.pop || 1]));
const counts = new Map();      // lowercased name → pick count
let boostMap = new Map();      // canonical NAME → boost number

// recompute ranks every function by (base popularity + weighted personal count),
// then maps the rank to a boost just BELOW the column/table sources (boost 2) so
// columns still win in expression slots, while functions order among themselves
// by frequency. Monotonic in weight, so the order also holds within any filtered
// subset (the date-prefixed functions, say).
function recompute() {
  const ranked = FUNCTIONS
    .map((f) => ({ name: f.name, weight: (basePop.get(f.name) || 1) + (counts.get(f.name.toLowerCase()) || 0) * 4 }))
    .sort((a, b) => (b.weight - a.weight) || (a.name < b.name ? -1 : 1));
  const m = new Map();
  ranked.forEach((r, i) => m.set(r.name, Math.max(-90, 1.5 - i * 0.5)));
  boostMap = m;
}
recompute(); // seed the default-popularity order at import (before login)

// funcBoost returns the live boost for a function name (canonical case).
export function funcBoost(name) {
  const b = boostMap.get(name);
  return b === undefined ? -90 : b;
}

let saveTimer = null;
function scheduleSave() {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(saveNow, SAVE_DEBOUNCE);
}
function saveNow() {
  saveTimer = null;
  const top = [...counts.entries()]
    .filter(([, c]) => c > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, SAVE_TOP_N);
  const obj = {};
  for (const [k, v] of top) obj[k] = v;
  api.put(`/api/me/prefs/${KEY}`, { value: JSON.stringify(obj) }).catch(() => {});
}

// bumpFuncUsage records one pick and re-ranks (cheap; debounced server save).
export function bumpFuncUsage(name) {
  const k = name.toLowerCase();
  counts.set(k, (counts.get(k) || 0) + 1);
  recompute();
  scheduleSave();
}

// loadFuncUsage seeds the counts from the saved pref. Fire-and-forget at login.
export async function loadFuncUsage() {
  try {
    const r = await api.get('/api/me/prefs');
    const raw = (r.prefs || {})[KEY];
    counts.clear();
    if (raw) {
      const obj = JSON.parse(raw);
      for (const k in obj) {
        const n = Number(obj[k]);
        if (Number.isFinite(n) && n > 0) counts.set(String(k).toLowerCase(), n);
      }
    }
    recompute();
  } catch {
    // keep the default-popularity ordering already seeded at import
  }
}

// resetFuncUsage clears personal history on logout so the next user starts from
// the default popularity order (and a pending save is dropped).
export function resetFuncUsage() {
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  counts.clear();
  recompute();
}
